// Filtres de recherche façon Outlook — expéditeur, destinataire, objet, plage de
// dates, pièces jointes, auteur du rangement.
//
// PORTÉE : CES FILTRES NE CONCERNENT QUE LES MAILS. Un expéditeur ou une date
// n'a aucun sens pour un dossier ou pour un espace, et le serveur écarte donc ces
// deux sections dès qu'un filtre est posé (cf. migration 0026). C'est un choix
// assumé : laisser remonter des projets sous un filtre « De : Jason » ferait
// croire le filtre inopérant.
//
// TOUT EST TEXTE DANS CE TYPE, y compris les dates. C'est la forme que produisent
// les champs de saisie, et la seule qui permette d'afficher un état intermédiaire
// (« 2026-07-3 » pendant la frappe) sans avoir à inventer une date invalide. La
// conversion en ISO — et le rejet de ce qui n'est pas une date — se fait au moment
// de construire la requête, en un seul endroit.

export interface SearchFilters {
  /** Expéditeur : comparé au NOM comme à l'adresse. */
  from: string;
  /** Destinataire : cherché dans les destinataires ET les copies. */
  to: string;
  subject: string;
  /** Date de début, au format `yyyy-mm-dd` (natif `<input type="date">`). */
  dateFrom: string;
  /** Date de fin INCLUSE côté utilisateur ; convertie en borne exclusive. */
  dateTo: string;
  /** `null` = indifférent ; `true` = avec pièce jointe ; `false` = sans. */
  hasAttachments: boolean | null;
  /** Nom d'une pièce jointe, extension comprise. */
  attachmentName: string;
  /** Personne qui a rangé le mail dans l'outil. */
  filedBy: string;
}

export const EMPTY_SEARCH_FILTERS: SearchFilters = {
  from: '',
  to: '',
  subject: '',
  dateFrom: '',
  dateTo: '',
  hasAttachments: null,
  attachmentName: '',
  filedBy: '',
};

/** Un filtre est-il réellement posé ? Sert à décider si la recherche est active. */
export function hasActiveFilters(f: SearchFilters): boolean {
  return (
    f.from.trim() !== '' ||
    f.to.trim() !== '' ||
    f.subject.trim() !== '' ||
    isDate(f.dateFrom) ||
    isDate(f.dateTo) ||
    f.hasAttachments !== null ||
    f.attachmentName.trim() !== '' ||
    f.filedBy.trim() !== ''
  );
}

/** Nombre de filtres posés — affiché en pastille sur le bouton de filtres. */
export function countActiveFilters(f: SearchFilters): number {
  let n = 0;
  if (f.from.trim()) n++;
  if (f.to.trim()) n++;
  if (f.subject.trim()) n++;
  if (isDate(f.dateFrom)) n++;
  if (isDate(f.dateTo)) n++;
  if (f.hasAttachments !== null) n++;
  if (f.attachmentName.trim()) n++;
  if (f.filedBy.trim()) n++;
  return n;
}

/**
 * `yyyy-mm-dd` COMPLET et valide.
 *
 * Le test de longueur seul ne suffit pas : `<input type="date">` peut rendre une
 * valeur partielle pendant la frappe sur certains navigateurs, et « 2026-02-31 »
 * est bien formé sans exister. `Date.parse` d'une date ISO seule est interprété
 * en UTC, donc stable quel que soit le fuseau.
 */
function isDate(v: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  // Rejette les dates qui « débordent » (31 février devenant le 3 mars).
  return d.toISOString().slice(0, 10) === v;
}

/**
 * Instant du DÉBUT DE JOURNÉE LOCALE pour un `yyyy-mm-dd`, décalé de `plusDays`.
 *
 * POURQUOI LOCAL ET NON UTC. Toute l'interface datte les mails en heure du poste
 * (`toLocaleDateString('fr-FR')`, cf. `formatMailDate`). Une borne posée à minuit
 * UTC tombe donc à 02:00 à Paris en été : un mail affiché « 27/07 à 01:30 »
 * échappait au filtre « du 27/07 », et un mail affiché « 28/07 à 01:00 » entrait
 * dans « au 27/07 ». Deux heures de décalage à chaque extrémité, invisibles à la
 * relecture et pourtant contraires à ce que l'utilisateur a sous les yeux — ce
 * sont les mails de nuit (rapports automatiques) qui trinquent.
 *
 * `new Date(y, m - 1, d)` est minuit dans le fuseau du poste, et le débordement
 * de mois comme d'année est absorbé par la conversion (32 juillet = 1er août).
 *
 * La forme `yyyy-mm-dd` est garantie par `isDate`, seule porte d'entrée des deux
 * appels ci-dessous — d'où le découpage par position plutôt qu'un `split`, que
 * TypeScript ne peut pas savoir long de trois éléments.
 */
function localDayStart(v: string, plusDays = 0): string {
  const y = Number(v.slice(0, 4));
  const m = Number(v.slice(5, 7));
  const d = Number(v.slice(8, 10));
  return new Date(y, m - 1, d + plusDays).toISOString();
}

/**
 * Traduit les filtres en corps de requête. Les champs vides sont OMIS — jamais
 * envoyés en chaîne vide, qui compterait côté serveur comme un filtre posé et
 * ferait disparaître espaces et dossiers sans raison visible.
 */
export function filtersToPayload(f: SearchFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.from.trim()) out.from = f.from.trim();
  if (f.to.trim()) out.to = f.to.trim();
  if (f.subject.trim()) out.subject = f.subject.trim();
  if (f.attachmentName.trim()) out.attachmentName = f.attachmentName.trim();
  if (f.filedBy.trim()) out.filedBy = f.filedBy.trim();
  if (f.hasAttachments !== null) out.hasAttachments = f.hasAttachments;
  // La plage saisie va du début de la journée de début au début du LENDEMAIN de
  // la journée de fin — journées LOCALES, cf. `localDayStart`.
  //
  // LE +1 JOUR EST LE POINT DÉLICAT : la borne haute est exclusive côté SQL. Sans
  // lui, choisir « au 16/07 » exclurait tout mail reçu le 16 après minuit — soit
  // la journée entière, le piège classique du filtre par date.
  if (isDate(f.dateFrom)) out.dateFrom = localDayStart(f.dateFrom);
  if (isDate(f.dateTo)) out.dateTo = localDayStart(f.dateTo, 1);
  return out;
}

/**
 * Clé stable pour le cache de requête. Un objet ne peut pas servir de clé
 * (identité changeante à chaque rendu) : on sérialise l'état EFFECTIF, donc deux
 * saisies équivalentes (« Jason » et « Jason  ») ne provoquent pas deux requêtes.
 */
export function filtersKey(f: SearchFilters): string {
  return JSON.stringify(filtersToPayload(f));
}
